/* eslint-disable no-param-reassign */
/* eslint-disable react/forbid-prop-types */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveBar } from '@nivo/bar';

import CustomLegend from '../../utils/chartTools/createLegend';
import customTooltip from '../../utils/chartTools/handleCustomTooltip';
import colorSelector from '../../utils/formatCharts/handleColor';
import {
  customPercentAxisRight,
  customPercentAxisLeft,
  customPercentAxisTop,
  customPercentAxisBottom,
  customIntervalAxisRight,
  customIntervalAxisLeft,
  customIntervalAxisTop,
  customIntervalAxisBottom,
} from '../../utils/formatCharts/formatAxis';
import { staticPercent } from '../../utils/formatCharts/handleCustomValue';
import { genDataHistogram } from '../../utils/formatCharts/handleData';
import KpiStatus from '../../utils/chartTools/KpiStatus';

const histogramDefaultPropGlobalTheme = {};

const Histogram = ({
  data, styleConfig, globalTheme = histogramDefaultPropGlobalTheme,
}) => {
  const [chartData, setChartData] = useState({ ready: false });

  useEffect(() => {
    setChartData(genDataHistogram(data, styleConfig));
  }, [
    data,
    styleConfig.DataFormatValueType,
  ]);

  const createTooltip = (val) => customTooltip(val, 'histogram', styleConfig);

  const createLabel = (label) => {
    const {
      LayoutControl, LabelFormatControl, Decimals, separadorNumericoCasas, DataFormatValueType,
    } = styleConfig;
    const val = DataFormatValueType === 'percentual' ? (
      staticPercent(label.value, Decimals, separadorNumericoCasas)
    ) : label.value;
    if (LayoutControl !== 'vertical' || LabelFormatControl === 'Central') return val;
    return <tspan y={-10}>{val}</tspan>;
  };

  return chartData.ready ? (
    <KpiStatus
      conditions={([
        [
          data?.index?.[0] === 'Totais', // TODO
        ],
      ])}
    >
      <ResponsiveBar
        data={chartData.data}
        keys={chartData.keys}
        indexBy="label"
        /* GENERAL */
        margin={styleConfig.Margin}
        layout={styleConfig.LayoutControl}
        padding={styleConfig?.PaddingControl?.value ?? 0}
        /* BORDER */
        borderWidth={styleConfig.BorderWidthControl.value}
        borderColor={styleConfig.BorderColorControl}
        /* LABEL */
        enableLabel={styleConfig.EnableLabelControl ? styleConfig.EnableLabelControl.checked : true}
        labelSkipWidth={styleConfig.labelSkipWidth || 0}
        labelSkipHeight={styleConfig.labelSkipHeight || 0}
        labelTextColor={styleConfig.LabelTextColorControl}
        label={createLabel}
        /* AXIS */
        enableGridX={styleConfig.EnableGridX}
        enableGridY={styleConfig.EnableGridY}
        axisTop={styleConfig.LayoutControl === 'horizontal' ? (
          customPercentAxisTop(styleConfig, null, false)
        ) : (
          customIntervalAxisTop(styleConfig)
        )}
        axisLeft={styleConfig.LayoutControl !== 'horizontal' ? (
          customPercentAxisLeft(styleConfig, null, false)
        ) : (
          customIntervalAxisLeft(styleConfig)
        )}
        axisBottom={styleConfig.LayoutControl === 'horizontal' ? (
          customPercentAxisBottom(styleConfig, null, false)
        ) : (
          customIntervalAxisBottom(styleConfig)
        )}
        axisRight={styleConfig.LayoutControl !== 'horizontal' ? (
          customPercentAxisRight(styleConfig, null, false)
        ) : (
          customIntervalAxisRight(styleConfig)
        )}
        /* LEGEND */
        layers={[
          'grid',
          'axes',
          'bars',
          'markers',
          styleConfig?.LegendControl?.checked ? (dt) => (
            <CustomLegend
              chartDataAr={chartData.keys}
              colorParts={dt.bars.reduce((aux, p) => {
                aux[p.data.id] = p.color;
                return aux;
              }, {})}
              sizes={{
                width: dt.innerWidth,
                height: dt.innerHeight,
              }}
              config={styleConfig}
              isEdit
            />
          ) : null,
          'annotations',
        ]}
        /* TOOLTIP */
        isInteractive={styleConfig.InteractiveControl}
        tooltip={createTooltip}
        /* STYLE */
        colors={colorSelector({
          dataLength: chartData.data.length,
          config: styleConfig,
          colorKey: 'id',
          globalTheme,
        })}
        /* THEME */
        theme={{
          legends: {
            text: {
              fontSize: `${styleConfig?.LegendsFontSizeControl?.value}px`,
              fontFamily: `'${styleConfig?.LegendsFontFamilyControl?.value}', Arial`,
              fill: styleConfig?.LegendFontColor ?? '#999',
              fontWeight: styleConfig?.LegendBold ? 'bold' : 'normal',
              fontStyle: styleConfig?.LegendItalic ? 'italic' : 'normal',
            },
            hidden: {
              text: {
                textDecoration: 'line-through',
              },
            },
          },
          labels: {
            text: {
              fontSize: `${styleConfig?.LabelsFontSizeControl ? styleConfig?.LabelsFontSizeControl.value : 13}px`,
              fontFamily: `'${styleConfig?.LabelsFontFamilyControl?.value}', Arial`,
              transform: `translate(${styleConfig?.LabelTranslateX ? styleConfig?.LabelTranslateX : 0}px, ${styleConfig?.LabelTranslateY ? styleConfig?.LabelTranslateY : 0}px) rotate(${styleConfig?.LabelRotationControl ? styleConfig?.LabelRotationControl : 0}deg)`,
              transformOrigin: 'center center',
              transformBox: 'fill-box',
            },
          },
          axis: {
            legend: {
              text: {
                fontSize: styleConfig.AxisGlobal.legendFontSize,
                fontFamily: `'${styleConfig.AxisGlobal.fontFamily}', Arial`,
                fill: styleConfig.AxisGlobal.color,
              },
            },
            ticks: {
              text: {
                fontFamily: `'${styleConfig.AxisGlobal.fontFamily}', Arial`,
                fontSize: styleConfig.AxisGlobal.fontSize,
                fill: styleConfig.AxisGlobal.color,
              },
            },
          },
        }}
        /* ETC */
        animate={styleConfig.InteractiveControl ? !!styleConfig.Animate : false}
        motionConfig={styleConfig.InteractiveControl ? (styleConfig.MotionConfig || 'default') : 'default'}
      />
    </KpiStatus>
  ) : <></>;
};

Histogram.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  styleConfig: PropTypes.objectOf(PropTypes.any).isRequired,
  globalTheme: PropTypes.objectOf(PropTypes.any),
};

export default Histogram;
